// PersonTable.jsx

import React, { useMemo } from 'react';
import ExcelUploader from './ExcelUploader';
import PersonRow from './PersonRow';


function PersonTable({
  people = [],
  earningsData = [],
  userPeriodData = {},
  selectedNationality,
  updateEarnings,
  selectedPeriod,
  selectedYear,
  selectedMonth,
  onDeletePerson,
  onUpdatePerson,
  refreshData,
  firms = [],
  selectedFirm = 'MvETonTggSawoeyt3tgG',
}) {
  // Determine the number of weeks and starting week
  let numOfWeeks;
  let startWeek;

  if (selectedNationality === 'Hrvat') {
    if (selectedPeriod === 1) {
      numOfWeeks = 2; // Weeks 1 and 2
      startWeek = 1;
    } else if (selectedPeriod === 2) {
      numOfWeeks = 3; // Weeks 3, 4, and 5
      startWeek = 3;
    }
  } else {
    numOfWeeks = 5;
    startWeek = 1;
  }

  // Filter people based on selected nationality and firm, and exclude archived persons
  const filteredPeople = useMemo(() => {
    return people
      .filter((person) => {
        const nationalityMatch =
          selectedNationality === 'Sve' || person.nationality === selectedNationality;
        const firmMatch = selectedFirm === '' || selectedFirm === 'MvETonTggSawoeyt3tgG' || person.firmId === selectedFirm;
        const dateMatch =
          (person.startYear < selectedYear ||
            (person.startYear === selectedYear && person.startMonth <= selectedMonth)) &&
          (!person.archived ||
            person.archiveYear > selectedYear ||
            (person.archiveYear === selectedYear && person.archiveMonth > selectedMonth));
        const notArchived = !person.archived; // Dodani uvjet za arhivirane osobe
        return nationalityMatch && firmMatch && dateMatch && notArchived;
      })
      .sort((a, b) => {
        if (selectedFirm === '' || selectedFirm === 'MvETonTggSawoeyt3tgG') {
          // Sort by firm name first if no firm is selected or default firm is selected, then by person ID
          const firmA = firms.find(firm => firm.id === a.firmId)?.name || '';
          const firmB = firms.find(firm => firm.id === b.firmId)?.name || '';
          if (firmA === firmB) {
            return a.id.localeCompare(b.id);
          }
          return firmA.localeCompare(firmB);
        } else {
          // If a specific firm is selected, sort only by person ID
          return a.id.localeCompare(b.id);
        }
      });
  }, [people, selectedNationality, selectedFirm, selectedYear, selectedMonth, firms]);

  // Function to calculate sums
  const sums = useMemo(() => {
    const sums = {
      // Earnings per week
      ...Array.from({ length: numOfWeeks }, (_, i) => [`tjedan${startWeek + i}`, 0]).reduce(
        (acc, [key, value]) => {
          acc[key] = value;
          return acc;
        },
        {}
      ),
      // Other sums
      zaradaTotal: 0,
      feeIznos: 0,
      total1: 0,
      appKnjig: 0,
      doprinosi: 0,
      renta: 0,
      rentSmjestaj: 0,
      oprema: 0,
      akontacija: 0,
      gorivo: 0,
      ostalo: 0,
      bonus: 0,
      total2: 0,
      tarifa: 0, // Added
      depozit: 0, // Added
      struja: 0, // Added
      glovoTax: 0, // Added
    };

    filteredPeople.forEach((person) => {
      // Filter earnings for this person
      const personEarnings = earningsData.filter((earning) => earning.userId === person.id);

      // Funkcija za dohvaćanje vrijednosti iz periodData ili person objekta
      const getValue = (fieldName) => {
        const periodValue = userPeriodData[person.id]?.[fieldName];
        if (periodValue !== undefined) return parseFloat(periodValue) || 0;
        const personValue = person[fieldName];
        return parseFloat(personValue) || 0;
      };

      // Sum earnings per week
      personEarnings.forEach((earning) => {
        const weekNumber = earning.week;
        const weekKey = `tjedan${weekNumber}`;
        if (sums.hasOwnProperty(weekKey)) {
          sums[weekKey] += parseFloat(earning.amount) || 0;
        }
      });

      // Calculate zaradaTotal for this person
      const zaradaTotal = personEarnings.reduce(
        (sum, earning) => sum + (parseFloat(earning.amount) || 0),
        0
      );

      // Get all financial values using getValue function
      const fee = getValue('fee');
      const feeIznos = (zaradaTotal * fee) / 100;
      const total1 = zaradaTotal - feeIznos;

      // Get all deductions
      const appKnjig = getValue('appKnjig');
      const doprinosi = getValue('doprinosi');
      const renta = getValue('renta');
      const rentSmjestaj = getValue('rentSmjestaj');
      const oprema = getValue('oprema');
      const akontacija = getValue('akontacija');
      const gorivo = getValue('gorivo');
      const ostalo = getValue('ostalo');
      const bonus = getValue('bonus');

      // Get additional deductions for foreigners
      const tarifa = getValue('tarifa');
      const depozit = getValue('depozit');
      const struja = getValue('struja');
      const glovoTax = getValue('glovoTax');

      // Calculate total2
      const total2 =
        total1 -
        appKnjig -
        doprinosi -
        renta -
        rentSmjestaj -
        oprema -
        akontacija -
        gorivo -
        ostalo -
        (person.nationality === 'Stranac'
          ? tarifa + depozit + struja + glovoTax
          : 0) +
        bonus;

      // Ensure total2 is not negative
      const finalTotal2 = Math.max(0, total2);

      // Sum other fields
      sums.zaradaTotal += zaradaTotal;
      sums.feeIznos += feeIznos;
      sums.total1 += total1;
      sums.appKnjig += appKnjig;
      sums.doprinosi += doprinosi;
      sums.renta += renta;
      sums.rentSmjestaj += rentSmjestaj;
      sums.oprema += oprema;
      sums.akontacija += akontacija;
      sums.gorivo += gorivo;
      sums.ostalo += ostalo;
      sums.bonus += bonus;
      sums.total2 += finalTotal2;
      sums.tarifa += tarifa;
      sums.depozit += depozit;
      sums.struja += struja;
      sums.glovoTax += glovoTax;
    });

    return sums;
  }, [filteredPeople, earningsData, userPeriodData, numOfWeeks, startWeek]);

  return (
    <div className="person-table-container">
      {/* Prikaz broja osoba */}
      <div className="table-header">
        <span className="people-count">
          Prikazano osoba: {filteredPeople.length}
        </span>
      </div>

      <table className="person-table">
        <thead>
          <tr>
            <th>Ime</th>
            <th>Uređivanje</th>
            <th>Rider ID</th>
            <th>Grad</th>
            <th>Firma</th>
            <th>N</th>
            {[...Array(numOfWeeks)].map((_, i) => (
              <th key={i}>
                <ExcelUploader
                  onDataParsed={(data, filename) => {
                    const weekNumber = startWeek + i;
                    console.log(
                      `Uploading data for week ${weekNumber} with filename ${filename}`
                    );
                    updateEarnings(weekNumber, data, filename);
                  }}
                >
                  Tjedan {startWeek + i}
                </ExcelUploader>
              </th>
            ))}
            <th>Zarada Total</th>
            <th className="highlighted-header">Fee %</th>
            <th className="highlighted-header">FEE Iznos</th>
            <th className="highlighted-header">Total 1</th>
            <th>App Knjig.</th>
            <th>Doprinosi</th>
            <th>Renta</th>
            <th>Rent smještaj</th>
            <th>Tarifa</th>
            <th>O.T.</th>
            <th>Oprema</th>
            <th>Akontacija</th>
            <th>Gorivo</th>
            <th>Ostalo</th>
            <th>Bonus</th>
            <th className="highlighted-header">Total 2</th>
          </tr>

          {/* Summary row appears here */}
          {selectedNationality === 'Sve' && (
            <tr className="summary-row">
              <td>
                <strong>Suma</strong>
              </td>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
              {[...Array(numOfWeeks)].map((_, i) => {
                const weekKey = `tjedan${startWeek + i}`;
                return (
                  <td key={i}>
                    <strong>{sums[weekKey].toFixed(2)}</strong>
                  </td>
                );
              })}
              <td>
                <strong>{sums.zaradaTotal.toFixed(2)}</strong>
              </td>
              <td></td>
              <td>
                <strong>{sums.feeIznos.toFixed(2)}</strong>
              </td>
              <td>
                <strong>{sums.total1.toFixed(2)}</strong>
              </td>
              <td>
                <strong>{sums.appKnjig.toFixed(2)}</strong>
              </td>
              <td>
                <strong>{sums.doprinosi.toFixed(2)}</strong>
              </td>
              <td>
                <strong>{sums.renta.toFixed(2)}</strong>
              </td>
              <td>
                <strong>{sums.rentSmjestaj.toFixed(2)}</strong>
              </td>
              <td><strong>{sums.tarifa.toFixed(2)}</strong></td> 
              <td><strong>{sums.glovoTax.toFixed(2)}</strong></td> 
              <td>
                <strong>{sums.oprema.toFixed(2)}</strong>
              </td>
              <td>
                <strong>{sums.akontacija.toFixed(2)}</strong>
              </td>
              <td>
                <strong>{sums.gorivo.toFixed(2)}</strong>
              </td>
              <td>
                <strong>{sums.ostalo.toFixed(2)}</strong>
              </td>
              <td>
                <strong>{sums.bonus.toFixed(2)}</strong>
              </td>
              <td>
                <strong>{sums.total2.toFixed(2)}</strong>
              </td>
            </tr>
          )}
        </thead>
        <tbody>
          {filteredPeople.length > 0 ? (
            filteredPeople.map((person) => (
              <PersonRow
                key={person.id}
                person={person}
                earningsData={earningsData}
                numOfWeeks={numOfWeeks}
                startWeek={startWeek}
                selectedYear={selectedYear}
                selectedMonth={selectedMonth}
                selectedPeriod={selectedPeriod}
                userPeriodData={userPeriodData[person.id] || {}}
                onDeletePerson={onDeletePerson}
                onUpdatePerson={onUpdatePerson}
                refreshData={refreshData}
                firms={firms}
                selectedNationality={selectedNationality}
              />
            ))
          ) : (
            <tr>
              <td colSpan={numOfWeeks + 22} style={{ textAlign: 'center' }}>
                Nema prikazanih osoba.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export default PersonTable;
